import { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import type { ActivationGroup } from '@/features/activationGroup/types'
import { useBiddingContext } from '@/features/bidding/contexts/BiddingContext'
import { useInvalidateBidHistoriesQuery } from '@/features/bidding/hooks/useBidHistoriesPageQuery'
import { useInvalidateBidOverviewSummaryPageQuery } from '@/features/bidding/hooks/useBidOverviewSummaryPageQuery'
import { useInvalidateGroupedBidsPageQuery } from '@/features/bidding/hooks/useGroupedBidsPageQuery'
import { usePtuExpiration } from '@/features/bidding/hooks/usePtuExpiration'
import { useSaveBidsMutation } from '@/features/bidding/hooks/useSaveBidsMutation'
import type {
  BulkFillMode,
  ExtendedBidPtu,
  OfferedAndForecastAndSuggestedPtu,
  PtuForTheOfferVolumeDataGrid,
} from '@/features/bidding/types/bid'
import { calculateBulkFillCapacity } from '@/features/bidding/utils/calculations/calculateBulkFillCapacity'
import { parseVolumeBid } from '@/features/bidding/utils/clipboard'
import { getByCountry } from '@/features/bidding/utils/getBidConfig'
import { getPtusForTheDataGridRowsUsingCapacityBid } from '@/features/bidding/utils/getPtusForTheDataGridRowsUsingCapacityBid'
import type { GroupedBidParams } from '@/features/bidding/utils/groupedBidParams/buildBiddingLinks'
import { bidFromOfferedAndForecastPtus } from '@/features/bidding/utils/model/createBid'
import { logMessageInCoralogix } from '@/features/coralogix/utils/sendExceptions'

import type { UseBidOfferCapacityBidsResult } from './useBidOfferCapacityBids'
import type { EnrichedPtuData } from './useBidOfferEnrichedPtus'

type UseBidOfferActionsParams = {
  groupParams: GroupedBidParams
  eligibleActivationGroups: ActivationGroup[] | undefined
  enrichedPtusByActivationGroup: Map<string, EnrichedPtuData>
  currentPtusByActivationGroup: Map<string, OfferedAndForecastAndSuggestedPtu[]>
  updatePtusForActivationGroup: (activationGroupUuid: string, ptus: OfferedAndForecastAndSuggestedPtu[]) => void
  updateSinglePtu: (activationGroupUuid: string, newPtu: OfferedAndForecastAndSuggestedPtu) => void
  capacityBids: UseBidOfferCapacityBidsResult
}

export type UseBidOfferActionsResult = {
  isSavingAllDrafts: boolean
  handleSaveAllClick: () => Promise<void>
  /** Save current draft values for a specific activation group */
  saveDraftForActivationGroup: (activationGroupUuid: string) => Promise<void>
  /** Update a single PTU (used by DataGrid row update) */
  processBidPtuUpdate: (
    activationGroupUuid: string,
    newPtu: OfferedAndForecastAndSuggestedPtu,
  ) => Promise<OfferedAndForecastAndSuggestedPtu>
  /** Fill all editable PTUs with a bulk value */
  fillAllPtusForActivationGroup: (
    activationGroupUuid: string,
    bulkMode: BulkFillMode,
    bulkFillValue: number | undefined,
  ) => Promise<void>
  /** Copy PTUs from a past bid */
  copyPastPtusForActivationGroup: (activationGroupUuid: string, pastPtusToCopy: ExtendedBidPtu[]) => Promise<void>
  /** Handle PTUs pasted from clipboard */
  handleClipboardPaste: (activationGroupUuid: string, clipboardText: string) => Promise<void>
  /** Get computed values for DataGrid display */
  getDataGridValues: (activationGroupUuid: string) => {
    acceptsOnlyPositiveInteger: boolean
    ptusForTheDataGridRows: PtuForTheOfferVolumeDataGrid[]
    volumeUnit: string
  }
}

export function useBidOfferActions({
  groupParams,
  eligibleActivationGroups,
  enrichedPtusByActivationGroup,
  currentPtusByActivationGroup,
  updatePtusForActivationGroup,
  updateSinglePtu,
  capacityBids,
}: UseBidOfferActionsParams): UseBidOfferActionsResult {
  const { t } = useTranslation()
  const { saveBids, isPending } = useSaveBidsMutation()
  const invalidateBidHistories = useInvalidateBidHistoriesQuery()
  const invalidateGroupedBids = useInvalidateGroupedBidsPageQuery()
  const invalidateBidOverview = useInvalidateBidOverviewSummaryPageQuery()
  const { pushAlert } = useAlertContext()
  const { selectedCountry } = useBiddingContext()
  const { isPtuEditable } = usePtuExpiration()
  const { marketsThatRequirePositiveIntegerVolume } = getByCountry(selectedCountry)

  // Use refs to avoid recreating callbacks when Maps change
  // This prevents unnecessary re-renders while ensuring callbacks always use fresh data
  const currentPtusRef = useRef(currentPtusByActivationGroup)
  currentPtusRef.current = currentPtusByActivationGroup

  const enrichedPtusRef = useRef(enrichedPtusByActivationGroup)
  enrichedPtusRef.current = enrichedPtusByActivationGroup

  const capacityBidsRef = useRef(capacityBids)
  capacityBidsRef.current = capacityBids

  const handleSaveAllClick = useCallback(async () => {
    if (!eligibleActivationGroups) {
      return
    }

    // Check if we have data for all activation groups
    const missingData = eligibleActivationGroups.filter((activationGroup) => {
      const enrichedData = enrichedPtusByActivationGroup.get(activationGroup.uuid)
      const currentPtus = currentPtusByActivationGroup.get(activationGroup.uuid)
      return !enrichedData || !currentPtus
    })

    if (missingData.length > 0) {
      const missingDataMessage = `Cannot save: missing data for ${missingData.length} activation groups:`
      const missingDataCodes = missingData.map((ag) => ag.code)
      logMessageInCoralogix(missingDataMessage, missingDataCodes)
      pushAlert({
        severity: 'error',
        message: t('bidding.create_bid.missing_data_for_activation_groups', {
          count: missingData.length,
          activationGroups: missingData.map((ag) => ag.code).join(', '),
        }),
      })
      return
    }

    // Create bids from current PTU state (not initial values!)
    const bids = eligibleActivationGroups
      .map((activationGroup) => {
        const enrichedData = enrichedPtusByActivationGroup.get(activationGroup.uuid)
        const currentPtus = currentPtusByActivationGroup.get(activationGroup.uuid)

        // At this point we know both exist due to the check above
        return bidFromOfferedAndForecastPtus(enrichedData!.bidMetaData, currentPtus!)
      })
      .filter((bid) => bid !== null)

    try {
      // Save all bids
      await saveBids(bids)

      // Invalidate queries to refresh data
      await Promise.all([invalidateBidHistories(), invalidateGroupedBids(), invalidateBidOverview()])

      // Show success message
      pushAlert({
        severity: 'success',
        message: t('bidding.create_bid.drafts_saved_successfully'),
      })
    } catch {
      // Error is already handled by useSaveBidsMutation's onError callback
      // which displays the error alert to the user
      // We just need to prevent the success message from showing
    }
  }, [
    groupParams,
    eligibleActivationGroups,
    enrichedPtusByActivationGroup,
    currentPtusByActivationGroup,
    saveBids,
    invalidateBidHistories,
    invalidateGroupedBids,
    invalidateBidOverview,
    pushAlert,
    t,
  ])

  /**
   * Helper to get bidMetaData for an activation group
   */
  const getBidMetaData = useCallback((activationGroupUuid: string) => {
    const enrichedData = enrichedPtusRef.current.get(activationGroupUuid)
    return enrichedData?.bidMetaData
  }, [])

  /**
   * Helper to save draft for a specific activation group
   */
  const saveDraftInternal = useCallback(
    async (
      activationGroupUuid: string,
      newBidPtus: OfferedAndForecastAndSuggestedPtu[],
      options?: { skipInvalidation?: boolean },
    ) => {
      const bidMetaData = getBidMetaData(activationGroupUuid)
      if (!bidMetaData) {
        const missingBidMetaDataMessage = `Cannot save draft: missing bidMetaData for activation group ${activationGroupUuid}`
        logMessageInCoralogix(missingBidMetaDataMessage)
        return
      }

      await saveBids([bidFromOfferedAndForecastPtus(bidMetaData, newBidPtus)])

      if (!options?.skipInvalidation) {
        await invalidateBidHistories()
        await invalidateGroupedBids()
        await invalidateBidOverview()
      }
    },
    [getBidMetaData, saveBids, invalidateBidHistories, invalidateGroupedBids, invalidateBidOverview],
  )

  /**
   * Save current draft values for a specific activation group
   */
  const saveDraftForActivationGroup = useCallback(
    async (activationGroupUuid: string) => {
      const ptus = currentPtusRef.current.get(activationGroupUuid) ?? []
      await saveDraftInternal(activationGroupUuid, ptus)
      pushAlert({
        message: t('bidding.create_bid.draft_saved_successfully', { activationGroup: activationGroupUuid }),
        severity: 'success',
      })
    },
    [saveDraftInternal, pushAlert, t],
  )

  /**
   * Update a single PTU (used by DataGrid processRowUpdate)
   */
  const processBidPtuUpdate = useCallback(
    async (
      activationGroupUuid: string,
      newPtu: OfferedAndForecastAndSuggestedPtu,
    ): Promise<OfferedAndForecastAndSuggestedPtu> => {
      const value = Number(newPtu.offeredVolume.quantity)
      const isValid = Number.isInteger(value) && value >= 0
      if (!isValid) {
        throw new Error(`We expect the value ${value} to be an integer greater or equal to 0`)
      }

      // Update context immediately for responsive UI (single PTU update)
      updateSinglePtu(activationGroupUuid, newPtu)

      // Build updated PTUs for saving - read current state and apply the update
      const currentPtus = currentPtusRef.current.get(activationGroupUuid) ?? []
      const updatedPtus = currentPtus.map((ptu) =>
        ptu.ptu.start.equals(newPtu.ptu.start) ? { ...ptu, offeredVolume: newPtu.offeredVolume } : ptu,
      )

      // Save in background without blocking the DataGrid from exiting edit mode
      saveDraftInternal(activationGroupUuid, updatedPtus, { skipInvalidation: true })
        .then(() => {
          pushAlert({
            message: t('bidding.create_bid.draft_saved_successfully', {
              activationGroup: activationGroupUuid,
            }),
            severity: 'success',
          })
        })
        .catch((error) => {
          logMessageInCoralogix(error)
        })

      // Return immediately so DataGrid can exit edit mode without waiting for API call
      return newPtu
    },
    [updateSinglePtu, saveDraftInternal, pushAlert, t],
  )

  /**
   * Fill all editable PTUs with a bulk value
   */
  const fillAllPtusForActivationGroup = useCallback(
    async (activationGroupUuid: string, bulkMode: BulkFillMode, bulkFillValue: number | undefined) => {
      if (bulkFillValue === undefined) return

      const bidMetaData = getBidMetaData(activationGroupUuid)
      if (!bidMetaData) return

      const acceptsOnlyPositiveInteger = marketsThatRequirePositiveIntegerVolume.includes(bidMetaData.marketProgram)
      const ptus = currentPtusRef.current.get(activationGroupUuid) ?? []
      const ptusForGrid = getPtusForTheDataGridRowsUsingCapacityBid(
        ptus,
        capacityBidsRef.current.hasCapacityBidOnThisDay(activationGroupUuid),
        getVolumeUnit(ptus),
      )

      const newBidPtus: OfferedAndForecastAndSuggestedPtu[] = ptusForGrid.map((ptu) => {
        if (!isPtuEditable(ptu.ptu.start, bidMetaData.bidType, bidMetaData.marketProgram)) return ptu

        const capacityWatts = calculateBulkFillCapacity({
          bulkMode,
          ptu,
          bulkFillValue,
          needsToRoundDown: acceptsOnlyPositiveInteger,
        })
        if (capacityWatts === null) return ptu

        return { ...ptu, offeredVolume: { quantity: capacityWatts, unit: 'WATTS' } }
      })

      updatePtusForActivationGroup(activationGroupUuid, newBidPtus)
      await saveDraftInternal(activationGroupUuid, newBidPtus)
      pushAlert({
        message: t('bidding.create_bid.draft_saved_successfully', { activationGroup: activationGroupUuid }),
        severity: 'success',
      })
    },
    [
      getBidMetaData,
      marketsThatRequirePositiveIntegerVolume,
      isPtuEditable,
      updatePtusForActivationGroup,
      saveDraftInternal,
      pushAlert,
      t,
    ],
  )

  /**
   * Copy PTUs from a past bid
   */
  const copyPastPtusForActivationGroup = useCallback(
    async (activationGroupUuid: string, pastPtusToCopy: ExtendedBidPtu[]) => {
      if (!pastPtusToCopy || pastPtusToCopy.length === 0) {
        return
      }

      const bidMetaData = getBidMetaData(activationGroupUuid)
      if (!bidMetaData) return

      const ptus = currentPtusRef.current.get(activationGroupUuid) ?? []
      const newBidPtus = ptus.map((ptu): OfferedAndForecastAndSuggestedPtu => {
        // Expired PTUs must keep the baseline value the reset logic maintains; copying past volumes
        // onto them only creates a draft that immediately gets corrected.
        if (!isPtuEditable(ptu.ptu.start, bidMetaData.bidType, bidMetaData.marketProgram)) return ptu

        const pastPtu = pastPtusToCopy.find((pastPtu) => pastPtu.ptu.start.hour === ptu.ptu.start.hour)
        return !pastPtu ? ptu : { ...ptu, offeredVolume: pastPtu.offeredVolume }
      })

      updatePtusForActivationGroup(activationGroupUuid, newBidPtus)
      await saveDraftInternal(activationGroupUuid, newBidPtus)

      pushAlert({
        message: t('bidding.create_bid.bid_copied_successfully'),
        severity: 'success',
      })
    },
    [getBidMetaData, isPtuEditable, updatePtusForActivationGroup, saveDraftInternal, pushAlert, t],
  )

  /**
   * Handle PTUs pasted from clipboard
   */
  const handleClipboardPaste = useCallback(
    async (activationGroupUuid: string, clipboardText: string) => {
      const parsedValues = parseVolumeBid(clipboardText)
      if (parsedValues.length === 0) return

      const bidMetaData = getBidMetaData(activationGroupUuid)
      if (!bidMetaData) return

      const acceptsOnlyPositiveInteger = marketsThatRequirePositiveIntegerVolume.includes(bidMetaData.marketProgram)
      const ptus = currentPtusRef.current.get(activationGroupUuid) ?? []

      let valuesToBeUsed = parsedValues
      if (acceptsOnlyPositiveInteger) {
        valuesToBeUsed = parsedValues.map((value) => Math.floor(value / 1_000_000) * 1_000_000)
      }

      if (valuesToBeUsed.length !== ptus.length) {
        pushAlert({
          message: t('bidding.create_bid.ptus_pasted_error', {
            expected: ptus.length,
            given: valuesToBeUsed.length,
          }),
          severity: 'error',
        })
        return
      }

      const newBidPtus = ptus.map((ptu, index): OfferedAndForecastAndSuggestedPtu => {
        if (!isPtuEditable(ptu.ptu.start, bidMetaData.bidType, bidMetaData.marketProgram)) return ptu
        return { ...ptu, offeredVolume: { quantity: valuesToBeUsed[index], unit: ptu.offeredVolume.unit } }
      })

      updatePtusForActivationGroup(activationGroupUuid, newBidPtus)
      await saveDraftInternal(activationGroupUuid, newBidPtus)

      pushAlert({
        message: t('bidding.create_bid.ptus_pasted_successfully'),
        severity: 'success',
      })
    },
    [
      getBidMetaData,
      marketsThatRequirePositiveIntegerVolume,
      isPtuEditable,
      updatePtusForActivationGroup,
      saveDraftInternal,
      pushAlert,
      t,
    ],
  )

  /**
   * Get computed values for DataGrid display
   */
  const getDataGridValues = useCallback(
    (activationGroupUuid: string) => {
      const bidMetaData = getBidMetaData(activationGroupUuid)
      const currentPtus = currentPtusRef.current.get(activationGroupUuid) ?? []
      const volumeUnit = getVolumeUnit(currentPtus)

      const acceptsOnlyPositiveInteger = bidMetaData
        ? marketsThatRequirePositiveIntegerVolume.includes(bidMetaData.marketProgram)
        : false

      const ptusForTheDataGridRows = getPtusForTheDataGridRowsUsingCapacityBid(
        currentPtus,
        capacityBidsRef.current.hasCapacityBidOnThisDay(activationGroupUuid),
        volumeUnit,
      )

      return {
        acceptsOnlyPositiveInteger,
        ptusForTheDataGridRows,
        volumeUnit,
      }
    },
    [getBidMetaData, marketsThatRequirePositiveIntegerVolume],
  )

  return useMemo(
    () => ({
      isSavingAllDrafts: isPending,
      handleSaveAllClick,
      saveDraftForActivationGroup,
      processBidPtuUpdate,
      fillAllPtusForActivationGroup,
      copyPastPtusForActivationGroup,
      handleClipboardPaste,
      getDataGridValues,
    }),
    [
      isPending,
      handleSaveAllClick,
      saveDraftForActivationGroup,
      processBidPtuUpdate,
      fillAllPtusForActivationGroup,
      copyPastPtusForActivationGroup,
      handleClipboardPaste,
      getDataGridValues,
    ],
  )
}

const getVolumeUnit = (ptus: PtuForTheOfferVolumeDataGrid[]): string =>
  !ptus || ptus.length === 0 ? '' : ptus[0].offeredVolume.unit
